/**
 * Auth Service - Client-side authentication management
 *
 * Handles login, logout, registration, and session state.
 * Uses HTTP-only cookies for session management.
 */

import type {
  AuthState,
  LoginRequest,
  RegistrationRequest,
  AuthResult,
  StoredAuthInfo,
  CurrentUserResponse,
} from "./types";
import { INITIAL_AUTH_STATE, UNAUTHENTICATED_STATE } from "./types";
import { apiBase } from "../api-config";

// Local storage key for auth info
const AUTH_STORAGE_KEY = "dafne_auth";

// API base URL (dynamic for dev vs prod)
const API_BASE = apiBase;

// Event target for auth state changes
type AuthStateListener = (state: AuthState) => void;

class AuthService {
  private _state: AuthState = INITIAL_AUTH_STATE;
  private _listeners: Set<AuthStateListener> = new Set();
  private _initialized = false;

  /**
   * Current auth state
   */
  get state(): AuthState {
    return this._state;
  }

  /**
   * Whether user is authenticated
   */
  get isAuthenticated(): boolean {
    return this._state.isAuthenticated;
  }

  /**
   * Current user ID (if authenticated)
   */
  get userId(): string | undefined {
    return this._state.userId;
  }

  /**
   * Subscribe to auth state changes
   */
  subscribe(listener: AuthStateListener): () => void {
    this._listeners.add(listener);
    // Immediately call with current state
    listener(this._state);

    // Return unsubscribe function
    return () => {
      this._listeners.delete(listener);
    };
  }

  /**
   * Update state and notify listeners
   */
  private setState(newState: AuthState): void {
    this._state = newState;
    this._listeners.forEach((listener) => listener(newState));
  }

  /**
   * Initialize auth service - check for existing session
   */
  async initialize(): Promise<AuthState> {
    if (this._initialized) {
      return this._state;
    }

    this._initialized = true;

    // First check localStorage for cached auth info
    const stored = this.getStoredAuthInfo();

    if (stored) {
      // Check if not expired locally
      const expiresAt = new Date(stored.expiresAt);
      if (expiresAt > new Date()) {
        // Optimistically set authenticated state
        this.setState({
          isAuthenticated: true,
          userId: stored.userId,
          email: stored.email,
          displayName: stored.displayName,
          expiresAt,
          isLoading: true,
        });
      }
    }

    // Verify with server (cookie-based)
    try {
      const serverState = await this.getCurrentUser();
      this.setState(serverState);

      if (serverState.isAuthenticated && serverState.userId) {
        // Update stored info
        this.setStoredAuthInfo({
          userId: serverState.userId,
          email: serverState.email!,
          displayName: serverState.displayName!,
          expiresAt: serverState.expiresAt!.toISOString(),
          roles: serverState.roles || [],
          isAdmin: serverState.isAdmin || false,
        });
      } else {
        // Clear stored info if server says not authenticated
        this.clearStoredAuthInfo();
      }

      return serverState;
    } catch {
      // Offline or error - use local state if available
      if (stored) {
        const expiresAt = new Date(stored.expiresAt);
        if (expiresAt > new Date()) {
          const offlineState: AuthState = {
            isAuthenticated: true,
            userId: stored.userId,
            email: stored.email,
            displayName: stored.displayName,
            expiresAt,
            roles: stored.roles || [],
            isAdmin: stored.isAdmin || false,
            isLoading: false,
          };
          this.setState(offlineState);
          return offlineState;
        }
      }

      this.setState(UNAUTHENTICATED_STATE);
      return UNAUTHENTICATED_STATE;
    }
  }

  /**
   * Check current user status with server
   */
  async getCurrentUser(): Promise<AuthState> {
    try {
      const response = await fetch(`${API_BASE}/api/login/me`, {
        method: "GET",
        credentials: "include",
        headers: {
          Accept: "application/json",
        },
      });

      if (!response.ok) {
        return UNAUTHENTICATED_STATE;
      }

      const data: CurrentUserResponse = await response.json();

      if (!data.isAuthenticated) {
        return UNAUTHENTICATED_STATE;
      }

      return {
        isAuthenticated: true,
        userId: data.userId,
        email: data.email,
        displayName: data.displayName,
        expiresAt: data.expiresAt ? new Date(data.expiresAt) : undefined,
        roles: data.roles || [],
        isAdmin: data.isAdmin || false,
        isLoading: false,
      };
    } catch {
      // Network error - cannot determine auth state
      throw new Error("Cannot connect to server");
    }
  }

  /**
   * Login with email and password
   */
  async login(request: LoginRequest): Promise<AuthResult> {
    try {
      const response = await fetch(`${API_BASE}/api/login`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          email: request.email,
          password: request.password,
          staySignedIn: request.staySignedIn ?? true,
        }),
      });

      const result: AuthResult = await response.json();

      if (result.success && result.credentials) {
        const authState: AuthState = {
          isAuthenticated: true,
          userId: result.credentials.userId,
          email: result.credentials.email,
          displayName: result.credentials.displayName,
          expiresAt: new Date(result.credentials.expiresUtc),
          roles: result.credentials.roles || [],
          isAdmin: result.credentials.isAdmin || false,
          isLoading: false,
        };

        this.setState(authState);

        this.setStoredAuthInfo({
          userId: result.credentials.userId,
          email: result.credentials.email,
          displayName: result.credentials.displayName,
          expiresAt: result.credentials.expiresUtc,
          roles: result.credentials.roles || [],
          isAdmin: result.credentials.isAdmin || false,
        });
      }

      return result;
    } catch (error) {
      return {
        success: false,
        errorMessage: "Network error. Please check your connection.",
        errorReason: "unknown_error",
      };
    }
  }

  /**
   * Register a new account
   */
  async register(request: RegistrationRequest): Promise<AuthResult> {
    try {
      const response = await fetch(`${API_BASE}/api/login/register`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          email: request.email,
          password: request.password,
          displayName: request.displayName,
          roles: request.roles || ["diabetic"],
        }),
      });

      const result: AuthResult = await response.json();

      if (result.success && result.credentials) {
        const authState: AuthState = {
          isAuthenticated: true,
          userId: result.credentials.userId,
          email: result.credentials.email,
          displayName: result.credentials.displayName,
          expiresAt: new Date(result.credentials.expiresUtc),
          roles: result.credentials.roles || [],
          isAdmin: result.credentials.isAdmin || false,
          isLoading: false,
        };

        this.setState(authState);

        this.setStoredAuthInfo({
          userId: result.credentials.userId,
          email: result.credentials.email,
          displayName: result.credentials.displayName,
          expiresAt: result.credentials.expiresUtc,
          roles: result.credentials.roles || [],
          isAdmin: result.credentials.isAdmin || false,
        });
      }

      return result;
    } catch (error) {
      return {
        success: false,
        errorMessage: "Network error. Please check your connection.",
        errorReason: "unknown_error",
      };
    }
  }

  /**
   * Logout - clear session
   */
  async logout(): Promise<void> {
    try {
      await fetch(`${API_BASE}/api/login/logout`, {
        method: "POST",
        credentials: "include",
      });
    } catch {
      // Ignore errors - clear local state anyway
    }

    this.clearStoredAuthInfo();
    this.setState(UNAUTHENTICATED_STATE);
  }

  /**
   * Request password reset email
   */
  async forgotPassword(
    email: string,
  ): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await fetch(`${API_BASE}/api/login/forgot-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ email }),
      });

      const result = await response.json();
      return result;
    } catch {
      return {
        success: false,
        message: "Network error. Please try again.",
      };
    }
  }

  // --- Local Storage Helpers ---

  private getStoredAuthInfo(): StoredAuthInfo | null {
    try {
      const stored = localStorage.getItem(AUTH_STORAGE_KEY);
      if (!stored) return null;
      return JSON.parse(stored);
    } catch {
      return null;
    }
  }

  private setStoredAuthInfo(info: StoredAuthInfo): void {
    try {
      localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(info));
    } catch {
      // Ignore storage errors
    }
  }

  private clearStoredAuthInfo(): void {
    try {
      localStorage.removeItem(AUTH_STORAGE_KEY);
    } catch {
      // Ignore storage errors
    }
  }
}

// Singleton instance
export const authService = new AuthService();

// Re-export types
export * from "./types";
